"use client";

import { createContext, useContext } from "react";

/**
 * The Targomo API key for this deployment, handed down from the server.
 *
 * Read at **runtime** on the server (`process.env.TARGOMO_API_KEY`, set per
 * environment in the gitops overlay) and passed in here — deliberately NOT a
 * `NEXT_PUBLIC_*` variable. Those are inlined at `next build`, and one frontend
 * image serves both staging and prod, so a build-time value could not differ per
 * environment. Threading it through context instead lets each env set its own
 * domain-scoped key with no rebuild.
 *
 * Kept in its own tiny module (no `@targomo/core` import) so the tools tab can ask
 * "is the isochrone available?" without pulling ~half a megabyte of SDK into the
 * main bundle — the reason the old `targomoKey()` helper stood alone too.
 *
 * The key is public by necessity (the browser calls Targomo directly); scope it to
 * the deployment's domains in the Targomo dashboard.
 */
const TargomoKeyContext = createContext<string | null>(null);

export function TargomoKeyProvider({
  value,
  children,
}: {
  value: string | null;
  children: React.ReactNode;
}) {
  return (
    <TargomoKeyContext.Provider value={value}>
      {children}
    </TargomoKeyContext.Provider>
  );
}

/** The Targomo API key, or null when the deployment has none configured. */
export function useTargomoKey(): string | null {
  return useContext(TargomoKeyContext);
}
